
import { computed, defineComponent, onMounted, ref, watch, Ref } from 'vue';
import { NavigationGuardNext, Route } from 'vue-router';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import { readUserLocale } from '@/store/main/getters';
import { useUserComposable } from '@/composables/useUser';
import { DynamicList } from '@/interfaces/api/general/dynamicList';
import { usePermissionComposable } from '@/composables/usePermission';
import { FormItem } from '@/interfaces/lib/model';
import { DynamicListResultFilterCriteria } from '@/lib/filterCriteria';
import { Sds, SdsDynamicListResult } from '@/interfaces/model/general/sds';
import { Department } from '@/interfaces/model/general/department';
import { MAX_LIMIT, NO_TAG_ID, NO_DEPARTMENT_ID } from '@/lib/constant';
import { NewDynamicList } from '@/lib/constructor/model/general/dynamicList';
import { ReanalysisRequestType, StatusId } from '@/lib/enum';
import { getPdfDisplayOptions, getStatusList } from '@/lib/formItem';
import { removeLastPdfExtension } from '@/lib/formatter';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  formatToDate,
  formatDatePickerDay,
  isWithinHours,
} from '@/utils/datetime';
import { countString } from '@/utils/string';
import { splitTextWithLinks } from '@/utils/splitTextWithLinks';
import router from '@/router';
import { store } from '@/store/index';
import { required, isValidLength } from '@/lib/rule';
import {
  readAssertedCompanyId,
  readAssertedDepartments,
  readIsFeatureEnabled,
} from '@/store/main/getters';
import {
  dispatchCancelCreateSimpleExportRequest,
  dispatchCreateReanalysisSds,
  dispatchDeleteDynamicListResult,
  dispatchGetCompanySdsesCount,
  dispatchGetDynamicList,
  dispatchFavoriteDynamicLists,
  dispatchUnfavoriteDynamicLists,
  dispatchUpdateDynamicList,
  dispatchGetDynamicListResults,
  dispatchGetDynamicLists,
  dispatchGetExportCreateSimple,
  dispatchGetExportCreateSimpleV3,
  dispatchGetExportCreateSimpleV3_2,
  dispatchGetExportDynamicListResults,
  dispatchPostDynamicListResult,
  dispatchPutDynamicListResult,
} from '@/store/main/actionsMain';
import DynamicListResultTable from '@/views/pages/dynamicListDetail/DynamicListResultTable.vue';
import { UpdateDynamicListRequestBody } from '@/interfaces/api/general/dynamicList';

import SdsSearchForm from '@/views/dashboard/SdsSearchForm.vue';
import SdsTableDrawer from '@/views/dashboard/sdsDrawer/SdsTableDrawer.vue';
import { useSdsComposable, SortState } from '@/composables/useSds';

import { casNumberPattern } from '@/lib/regex';
import DownloadProgress from '@/views/dashboard/DownloadProgress.vue';
import { TagResponse } from '@/interfaces/model/general/tag';
import { useTagComposable } from '@/composables/useTag';
import { encodeGcsPathSegment } from '@/composables/useDownloadExportSdses';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { formatToDateTime } from '@/utils/datetime';
import sharedMessages from '@/lib/shared-messages';
import { regulationDatabaseUrl } from '@/env';
import {
  buildDbtTypeLabels,
  checkHasThreshold,
  buildMappedRegulationLinks,
} from '@/views/pages/dynamicList/templateUtils';
import SelectTags from '@/components/SelectTags.vue';
import SelectInTextField from '@/components/SelectInTextField.vue';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import { getFullName as getFullNameUtil } from '@/utils/string';
import TagChip from '@/components/TagChip.vue';
import { Law } from '@/interfaces/api/general/law';
import { Regulation } from '@/interfaces/api/general/regulation';
import { useMasterData } from '@/composables/useMasterData';
import ExtractionConditionsPanel from '@/views/pages/dynamicList/ExtractionConditionsPanel.vue';
import { hasRuleTreeConditions } from '@/views/pages/dynamicList/extractionConditions';

const locale = computed(() => readUserLocale(store));

const $t = i18n.t.bind(i18n);
const pdfDisplayOptions = getPdfDisplayOptions();
const statusList = getStatusList();

const m = defineMessages({
  backToList: '一覧に戻る',
  bulkExport: '一括エクスポート',
  checkDetailInChemicanRegulationDatabase: '法令データベースで詳細を確認',
  chemicanRegulationDatabase: '法令データベース：',
  dateUpdated: '更新日',
  extractionConditionUpdateHistory: '抽出条件更新履歴',
  selectedTemplate: '選択したチェックリスト',
  thresholdTooltip:
    '法令に定められた閾値（裾切値）を考慮してチェックを行います。閾値未満（法令によっては閾値以下）の含有量は対象外として扱われます。',
  updatedBy: '更新者',
  updatedContent: '更新内容',
});

export default defineComponent({
  components: {
    SdsTableDrawer,
    SdsSearchForm,
    SelectTags,
    SelectInTextField,
    DepartmentTreeSelect,
    TagChip,
    DynamicListResultTable,
    DownloadProgress,
    ExtractionConditionsPanel,
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.isEditing) {
      const isAllowed: boolean = window.confirm(
        $t(sharedMessages.confirmLeave)
      );
      if (isAllowed) {
        next();
      }
    } else {
      next();
    }
  },
  setup() {
    // ルートパラメータを初期化時にキャプチャ（ナビゲーション後にNaNになるのを防止）
    const dynamicListId = Number(router.currentRoute.params.id);

    const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

    const nextUpdateDate = () => {
      const japanNow = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })
      );

      japanNow.setDate(japanNow.getDate() + 1);
      japanNow.setHours(0, 30, 0, 0);

      const nextUpdateTime = new Date(
        japanNow.toLocaleString('en-US', {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      );

      return formatToDateTime(nextUpdateTime.toISOString());
    };
    const showSdsDetailView = ref<boolean>(false);
    const pdfName = ref<string>('');
    const keywordList = ref<Array<string>>([]);
    const productFormItems = ref<Array<{ text: string; value: number }>>([]);
    const materialFormItems = ref<Array<FormItem>>([]);
    const casFormItems = ref<Array<FormItem>>([]);
    const page = ref<number>(1);
    const limit = ref<number>(100);
    const isLoadingDetail = ref<boolean>(true);
    const isLoadingSdses = ref<boolean>(false);
    const isLoadingSds = ref<boolean>(false);
    const isEditing = ref<boolean>(false);
    const statusIdList = ref<Array<number>>([...StatusId.values()]);
    const dynamicList = ref<DynamicList>({ ...NewDynamicList });
    const uploadedTotal = ref<number>(0);
    const filteredItemCount = ref<number>(0);
    const lastCount = ref<number>(0);
    const companyFiles = ref<Sds[]>([]);
    const canExportCreateSimpleCount = ref<number>(0);
    const canExportCreateSimpleCountV3 = ref<number>(0);
    const dialogShow = ref<boolean>(false);
    const reanalysisSdsModalShow = ref<boolean>(false);
    const reanalysisSdsComment = ref<{
      comment: string;
      sds_id: string;
      requestType: string | null;
    }>({
      comment: '',
      sds_id: '',
      requestType: null,
    });

    const reanalysisRequestTypeItems = computed(() =>
      ReanalysisRequestType.values().map((type) => ({
        text: i18n.tc(type.messageId),
        value: type.value,
      }))
    );

    const reanalysisCommentPlaceholder = computed(() => {
      const selected = ReanalysisRequestType.values().find(
        (type) => type.value === reanalysisSdsComment.value.requestType
      );
      return selected
        ? i18n.tc(selected.commentPlaceholderMessageId)
        : i18n.tc(sharedMessages.selectRequestTypeFirst);
    });
    const activeSds = ref<Sds>();
    const departments = computed(() => readAssertedDepartments(store));

    // Check if new regulatory screening UI feature flag is enabled
    const useNewRegulatoryScreeningUI = computed(() => {
      return (
        readIsFeatureEnabled(store)('use_new_regulatory_screening_ui') ?? false
      );
    });

    // Check if dynamic list was created by Chemican admin (ケミカン運営)
    // When feature flag is OFF, admin-created checklists cannot be edited by users
    const isCreatedByChemicanAdmin = computed(() => {
      return (
        !dynamicList.value.created_by ||
        dynamicList.value.created_by === '' ||
        (dynamicList.value.created_user?.last_name === 'ケミカン' &&
          dynamicList.value.created_user?.first_name === '運営')
      );
    });

    // Validation constants
    const titleMaxLength = 256;
    const descriptionMaxLength = 10000;

    // Description editing state
    const isEditingDescription = ref(false);
    const editedDescription = ref('');
    const isLoadingDescription = ref(false);

    // Validation computed property for description
    const isDescriptionValid = computed(() => {
      const desc = editedDescription.value || '';
      const length = countString(desc);
      return length <= descriptionMaxLength;
    });

    // Laws and regulations data
    const { fetchAllLaws, fetchAllRegulations } = useMasterData();
    const laws = ref<Law[]>([]);
    const regulations = ref<Regulation[]>([]);

    const isChecklistLawRegulationEnabled = computed(() => {
      return (
        readIsFeatureEnabled(store)('use_new_regulatory_screening_ui') ?? false
      );
    });

    const isBeforeStart = computed(() => {
      const dl = dynamicList.value;
      if (!dl.is_query_ready) return true;
      if (
        isChecklistLawRegulationEnabled.value &&
        dl.total === 0 &&
        isWithinHours(dl.created_at, 24)
      ) {
        return true;
      }
      return false;
    });

    // Computed properties for extraction conditions display
    const lawNames = computed(() => {
      return laws.value.map((law) => law.name);
    });

    const regulationNames = computed(() => {
      return regulations.value.map((reg) => reg.name);
    });

    // Split description into parts for link rendering
    const descriptionParts = computed(() => {
      return splitTextWithLinks(dynamicList.value.description || '');
    });

    const dbtTypeLabelMap = {
      cas: $t(sharedMessages.dbtTypeCas),
      material: $t(sharedMessages.dbtTypeMaterial),
      outdated: $t(sharedMessages.dbtTypeOutdated),
      regulation: $t(sharedMessages.dbtTypeRegulation),
    };

    const hasThreshold = computed(() => {
      if (!dynamicList.value.dbt_types) return false;
      return checkHasThreshold(dynamicList.value.dbt_types);
    });

    const dbtTypeLabels = computed(() => {
      if (!dynamicList.value.dbt_types) return [];
      return buildDbtTypeLabels(dynamicList.value.dbt_types, dbtTypeLabelMap);
    });

    // ノーコード（rule_tree）で作ったチェックリストは、BEが組み立てた抽出条件が返る。
    // 返らないチェックリストは従来どおり law_ids / regulation_ids / dbt_types で表示する
    const ruleTreeConditions = computed(
      () => dynamicList.value.extraction_conditions || null
    );
    const hasRuleTree = computed(() =>
      hasRuleTreeConditions(ruleTreeConditions.value)
    );

    // Check if there are any extraction conditions to display
    const hasExtractionConditions = computed(() => {
      if (hasRuleTree.value) return true;
      return (
        lawNames.value.length > 0 ||
        regulationNames.value.length > 0 ||
        dbtTypeLabels.value.length > 0
      );
    });

    // Generate regulation database links from laws and regulations
    // 法令と規則から法令データベースリンクを生成
    const regulationDatabaseLinks = computed(() =>
      buildMappedRegulationLinks(
        laws.value,
        regulations.value,
        regulationDatabaseUrl
      )
    );

    // Methods for fetching laws and regulations
    const fetchLaws = async () => {
      if (
        !dynamicList.value.law_ids ||
        dynamicList.value.law_ids.length === 0
      ) {
        laws.value = [];
        return;
      }
      try {
        const allLaws = await fetchAllLaws();
        const lawIdSet = new Set(dynamicList.value.law_ids);
        laws.value = allLaws.filter((l) => lawIdSet.has(l.id));
      } catch (error) {
        console.error('Failed to fetch laws:', error);
        laws.value = [];
      }
    };

    const fetchRegulations = async () => {
      if (
        !dynamicList.value.regulation_ids ||
        dynamicList.value.regulation_ids.length === 0
      ) {
        regulations.value = [];
        return;
      }
      try {
        const allRegulations = await fetchAllRegulations();
        const regIdSet = new Set(dynamicList.value.regulation_ids);
        regulations.value = allRegulations.filter((r) => regIdSet.has(r.id));

        // 規則から参照される法令も追加で取得
        const allLaws = await fetchAllLaws();
        const regulationLawIds = regulations.value
          .map((reg) => reg.law_id)
          .filter((id): id is number => id !== undefined && id !== null);

        const existingLawIds = new Set(laws.value.map((law) => law.id));
        const additionalLaws = allLaws.filter(
          (l) => regulationLawIds.includes(l.id) && !existingLawIds.has(l.id)
        );
        if (additionalLaws.length > 0) {
          laws.value = [...laws.value, ...additionalLaws];
        }
      } catch (error) {
        console.error('Failed to fetch regulations:', error);
        regulations.value = [];
      }
    };

    // Description editing methods
    const startEditingDescription = () => {
      editedDescription.value = dynamicList.value.description || '';
      isEditingDescription.value = true;
    };

    const cancelEditingDescription = () => {
      isEditingDescription.value = false;
      editedDescription.value = '';
    };

    const saveDescription = async (): Promise<void> => {
      // Validate description length
      const descLength = countString(editedDescription.value || '');
      if (descLength > descriptionMaxLength) {
        return;
      }

      try {
        isLoadingDescription.value = true;
        const res = await dispatchUpdateDynamicList(store, {
          dynamicListId: dynamicList.value.id,
          data: {
            description: editedDescription.value,
          },
        });
        if (res) {
          await fetchDynamicList();
          isEditingDescription.value = false;
        }
      } catch (error) {
        console.error('Failed to update description:', error);
      } finally {
        isLoadingDescription.value = false;
      }
    };

    const {
      sds,
      sdses,
      total,
      pdfBlob,
      fetchCompanySds,
      fetchCompanySdses,
      fetchSdsPdf,
      fetchCompanySdsesCount,
      fetchCompanySdsesCountWithCondition,
      updateCompanySds,
    } = useSdsComposable();
    const { users, getCompanyUsers } = useUserComposable();

    const selectableStatusList: Array<StatusId> = StatusId.getDropdownOptions();
    const statusTextList: Array<string> = Array.from(
      new Set(selectableStatusList.map((status) => status.groupId))
    );

    const searchUserText = ref<string>('');
    const searchTagText = ref<string>('');

    const isDialogVisibleRevisedAtStartDate = ref(false);
    const isDialogVisibleRevisedAtEndDate = ref(false);
    const isDialogVisibleCreatedAtStartDate = ref(false);
    const isDialogVisibleCreatedAtEndDate = ref(false);
    const isDialogVisibleUpdatedAtStartDate = ref(false);
    const isDialogVisibleUpdatedAtEndDate = ref(false);

    const saveDate = (state) => {
      if (state == 1) {
        isDialogVisibleRevisedAtStartDate.value = false;
      }
      if (state == 2) {
        isDialogVisibleRevisedAtEndDate.value = false;
      }
      if (state == 3) {
        isDialogVisibleCreatedAtStartDate.value = false;
      }
      if (state == 4) {
        isDialogVisibleCreatedAtEndDate.value = false;
      }
      if (state == 5) {
        isDialogVisibleUpdatedAtStartDate.value = false;
      }
      if (state == 6) {
        isDialogVisibleUpdatedAtEndDate.value = false;
      }
    };

    const hasFilterCriteria = computed(() => {
      return (
        filterCriteria.value.assigneeUsers.length ||
        filterCriteria.value.tags.length ||
        filterCriteria.value.includeNoTag ||
        filterCriteria.value.departmentIds.length ||
        filterCriteria.value.pdfName ||
        filterCriteria.value.productName ||
        filterCriteria.value.cause ||
        filterCriteria.value.note ||
        filterCriteria.value.revisedAtStartDate ||
        filterCriteria.value.revisedAtEndDate ||
        filterCriteria.value.createdAtStartDate ||
        filterCriteria.value.createdAtEndDate ||
        filterCriteria.value.updatedAtStartDate ||
        filterCriteria.value.updatedAtEndDate ||
        filterCriteria.value.isNewSds ||
        filterCriteria.value.isNewChecklist ||
        filterCriteria.value.isCauseUpdated
      );
    });

    const resetFilters = async (): Promise<void> => {
      filterCriteria.value.assigneeUsers = [];
      filterCriteria.value.tags = [];
      filterCriteria.value.includeNoTag = false;
      filterCriteria.value.departmentIds = [];
      filterCriteria.value.pdfName = '';
      filterCriteria.value.productName = '';
      filterCriteria.value.cause = '';
      filterCriteria.value.note = '';
      filterCriteria.value.revisedAtStartDate = '';
      filterCriteria.value.revisedAtEndDate = '';
      filterCriteria.value.createdAtStartDate = '';
      filterCriteria.value.createdAtEndDate = '';
      filterCriteria.value.updatedAtStartDate = '';
      filterCriteria.value.updatedAtEndDate = '';
      filterCriteria.value.isNewSds = false;
      filterCriteria.value.isNewChecklist = false;
      filterCriteria.value.isCauseUpdated = false;
      tempFilterCriteria.value = { ...filterCriteria.value };
      applyFilters();
    };

    const cancelFilters = async (): Promise<void> => {
      isFilterPanelVisible.value = false;
    };

    const {
      getCompanyDepartments,
      companyDepartments,
      visibleCompanyDepartmentsByOwnOrRole,
      departmentTree,
      getCompanyDepartmentTree,
    } = useDepartmentComposable();

    const { tags, getCompanyTag } = useTagComposable();
    const dynamicLists = ref<Array<DynamicList>>([]);

    const isFilterPanelVisible = ref(false);

    const toggleFilterPane = (): void => {
      isFilterPanelVisible.value = !isFilterPanelVisible.value;
    };

    const uploadingToCompletedStatusIdList = ref<Array<number>>([
      ...StatusId.uploadingToCompletedValues(),
    ]);

    const cancelExportCreateSimple = (): void => {
      dispatchCancelCreateSimpleExportRequest(store);
      dialogShow.value = false;
    };

    const openReanalysisSdsModal = (sdsId: string): void => {
      reanalysisSdsModalShow.value = true;
      reanalysisSdsComment.value = {
        comment: '',
        sds_id: sdsId,
        requestType: null,
      };
    };

    const closeReanalysisSdsModal = (): void => {
      reanalysisSdsModalShow.value = false;
    };

    const reanalysisSds = async (): Promise<void> => {
      if (!reanalysisSdsComment.value.requestType) {
        return;
      }
      const payload = {
        sdsId: reanalysisSdsComment.value.sds_id,
        comment: reanalysisSdsComment.value.comment.trim(),
        requestType: reanalysisSdsComment.value.requestType,
      };
      await dispatchCreateReanalysisSds(store, payload);
      closeReanalysisSdsModal();

      await doFetchCompanySdses();
      await doFetchCompanySdsesCount();
      await doFetchCompanySdsesCountWithCondition();

      await doFetchCompanySds(reanalysisSdsComment.value.sds_id);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    watch(showSdsDetailView, (next, prev) => {
      if (next === false) {
        activeSds.value = undefined;
      }
    });

    const currentIndex = ref(0);
    const hasPrevSds = computed((): boolean => {
      return currentIndex.value > 0;
    });

    const hasNextSds = computed((): boolean => {
      return currentIndex.value < companyFiles.value.length - 1;
    });

    const showPrevSds = async (): Promise<void> => {
      const index = currentIndex.value - 1;

      showSdsDetail(companyFiles.value[index], index);
    };

    const showNextSds = async (): Promise<void> => {
      const index = currentIndex.value + 1;

      showSdsDetail(companyFiles.value[index], index);
    };

    const resetPagenation = (): void => {
      page.value = 1;
    };

    const resetSearchForm = (): void => {
      keywordList.value = [];
    };

    const showSdsDetail = async (item: Sds, index: number): Promise<void> => {
      await doFetchCompanySds(item.uuid);
      await doFetchPdf(item.uuid);

      showSdsDetailView.value = true;
      isLoadingSds.value = true;
      canExportCreateSimpleCount.value = 0;
      canExportCreateSimpleCountV3.value = 0;

      activeSds.value = item;
      currentIndex.value = index;

      productFormItems.value.splice(0);
      productFormItems.value.push(
        ...sds.value.sds_products.map((x, idx) => {
          return {
            text: x.name,
            value: idx,
          };
        })
      );
      materialFormItems.value.splice(0);
      casFormItems.value.splice(0);
      for (const product of sds.value.sds_products) {
        // material_list内のcas_listをcasNumberPatternでマッチングし、1つ以上マッチしたらcanExportCreateSimpleCountV3をインクリメント
        const casList = product.sds_section3.material_list
          .map((x) => x.cas_list)
          .flat()
          .filter((x) => x.cas_number.match(casNumberPattern));
        if (casList.length > 0) {
          canExportCreateSimpleCountV3.value++;
        }

        for (const material of product.sds_section3.material_list) {
          materialFormItems.value.push({
            text: material.material_name,
            value: material.material_name,
          });
          for (const cas of material.cas_list) {
            casFormItems.value.push({
              text: cas.cas_number,
              value: cas.cas_number,
            });
            if (cas.cas_number.match(casNumberPattern)) {
              canExportCreateSimpleCount.value++;
            }
          }
        }
      }

      isLoadingSds.value = false;
    };

    const doFetchPdf = async (sdsId: string): Promise<void> => {
      fetchSdsPdf({
        companyId: readAssertedCompanyId(store),
        sdsId: sdsId,
      });
    };

    const doFetchCompanySds = async (sdsId: string): Promise<void> => {
      await fetchCompanySds({
        sdsId: sdsId,
        companyId: readAssertedCompanyId(store),
      });
    };

    const doFetchCompanySdses = async (): Promise<void> => {
      isLoadingSdses.value = true;

      await fetchCompanySdses({
        companyId: readAssertedCompanyId(store),
        data: {
          limit: limit.value,
          page: page.value - 1,
          keyword_list: keywordList.value,
          status_id_list: statusIdList.value,
          has_product: true,
          has_section1: false,
          has_section2: false,
          has_section3: true,
          has_section8: false,
          has_section8_exposure_control: false,
          has_section9: false,
          has_section14: false,
          has_section15: false,
          has_assistant_log_cannot_analysis: false,
        },
      });

      isLoadingSdses.value = false;
    };

    const doFetchCompanySdsesCountWithCondition = async (): Promise<void> => {
      isLoadingSdses.value = true;

      await fetchCompanySdsesCountWithCondition({
        companyId: readAssertedCompanyId(store),
        data: {
          keyword_list: keywordList.value,
          status_id_list: statusIdList.value,
        },
      });

      isLoadingSdses.value = false;
    };

    const doFetchCompanySdsesCount = async (): Promise<void> => {
      isLoadingSdses.value = true;

      await fetchCompanySdsesCount({
        companyId: readAssertedCompanyId(store),
        data: {
          keyword_list: keywordList.value,
        },
      });

      isLoadingSdses.value = false;
    };

    const doUpdateCompanySds = async (
      sdsId: string,
      data: Sds,
      tags: TagResponse[],
      departments: Department[]
    ): Promise<void> => {
      await updateCompanySds(
        readAssertedCompanyId(store),
        sdsId,
        data,
        departments,
        tags,
        store.state.main.userProfile
      );
      await doFetchCompanySds(data.uuid);
      await doFetchCompanySdses();
      await doFetchCompanySdsesCount();
      await doFetchCompanySdsesCountWithCondition();
      await fetchDynamicListResults();
    };

    const downloadExportData = (): void => {
      const params = new URLSearchParams(location.href.split('?')[1]);
      const isExport = params.get('export');

      if (isExport) {
        const link = document.createElement('a');
        const bucket = params.get('bucket');
        const companyId = params.get('companyId');
        const filename = params.get('filename');
        const query = {
          'X-Goog-Algorithm': params.get('X-Goog-Algorithm') ?? '',
          'X-Goog-Credential': params.get('X-Goog-Credential') ?? '',
          'X-Goog-Date': params.get('X-Goog-Date') ?? '',
          'X-Goog-Expires': params.get('X-Goog-Expires') ?? '',
          'X-Goog-SignedHeaders': params.get('X-Goog-SignedHeaders') ?? '',
          'X-Goog-Signature': params.get('X-Goog-Signature') ?? '',
        };
        const urlSearchParam = new URLSearchParams(query).toString();
        const url = `https://storage.googleapis.com/${bucket}/export/${companyId}/${encodeGcsPathSegment(
          filename ?? ''
        )}?${urlSearchParam}`;
        link.href = url;
        link.download = 'SDS一覧.xlsx';
        link.click();
      }
    };

    const cancelEdit = async (sdsId: string): Promise<void> => {
      isLoadingSds.value = true;

      await doFetchCompanySds(sdsId);

      productFormItems.value.splice(0);
      productFormItems.value.push(
        ...sds.value.sds_products.map((x, idx) => {
          return {
            text: x.name,
            value: idx,
          };
        })
      );
      materialFormItems.value.splice(0);
      casFormItems.value.splice(0);
      for (const product of sds.value.sds_products) {
        for (const material of product.sds_section3.material_list) {
          materialFormItems.value.push({
            text: material.material_name,
            value: material.material_name,
          });
          for (const cas of material.cas_list) {
            casFormItems.value.push({
              text: cas.cas_number,
              value: cas.cas_number,
            });
          }
        }
      }

      isLoadingSds.value = false;
    };

    const confirmSave = (event): void => {
      if (isEditing.value) {
        event.preventDefault();
        event.returnValue = $t(sharedMessages.confirmLeave);
      }
    };

    const fetchCompanySdsesCountWithAll = async (): Promise<void> => {
      const res = await dispatchGetCompanySdsesCount(store, {
        companyId: readAssertedCompanyId(store),
        data: {
          keyword_list: keywordList.value,
          status_id_list: uploadingToCompletedStatusIdList.value,
        },
      });
      if (res) {
        uploadedTotal.value = res.data.total;
      }
    };

    const fetchDynamicList = async () => {
      const res = await dispatchGetDynamicList(store, {
        dynamicListId: dynamicListId,
      });
      if (res && res.data) {
        dynamicList.value = res.data;
        lastCount.value = res.data.last_count;
        editingDynamicList.value = { ...dynamicList.value };
        // Fetch laws first, then regulations (regulations may need to fetch additional laws)
        // 法令を先に取得し、その後規則を取得（規則から追加の法令を取得する可能性があるため）
        await fetchLaws();
        await fetchRegulations();
      }
    };

    const isEditingDynamicList = ref<boolean>(false);
    const isLoadingDynamicList = ref<boolean>(false);
    const editingDynamicList = ref<UpdateDynamicListRequestBody>({});
    const isNameComposing = ref<boolean>(false);

    // SE-572: お気に入り(★) トグル
    // UX指摘①-B: 登録は確認ダイアログ、解除は即時実行＋Undo(元に戻す)トースト
    const favoriteDialogOpen = ref<boolean>(false);
    const isFavoriting = ref<boolean>(false);
    const isUndoSnackbarVisible = ref<boolean>(false);

    // ボタン押下: 未登録→登録ダイアログ / 登録済み→即時解除
    const onFavoriteButtonClick = async () => {
      if (!dynamicList.value.is_favorite) {
        favoriteDialogOpen.value = true;
        return;
      }
      isFavoriting.value = true;
      try {
        await dispatchUnfavoriteDynamicLists(store, {
          dynamicListIds: [dynamicListId],
        });
        await fetchDynamicList();
        isUndoSnackbarVisible.value = true;
      } finally {
        isFavoriting.value = false;
      }
    };

    // 登録ダイアログの確定（登録専用）
    const confirmFavorite = async () => {
      isFavoriting.value = true;
      try {
        await dispatchFavoriteDynamicLists(store, {
          dynamicListIds: [dynamicListId],
        });
        await fetchDynamicList();
      } finally {
        isFavoriting.value = false;
        favoriteDialogOpen.value = false;
      }
    };

    // Undo（元に戻す）: 再登録（成功通知は action 側で表示）
    const undoUnfavorite = async () => {
      isUndoSnackbarVisible.value = false;
      isFavoriting.value = true;
      try {
        await dispatchFavoriteDynamicLists(store, {
          dynamicListIds: [dynamicListId],
        });
        await fetchDynamicList();
      } finally {
        isFavoriting.value = false;
      }
    };

    // Validation computed property for title
    const isTitleValid = computed(() => {
      const name = editingDynamicList.value.name || '';
      const length = countString(name);
      return name.length > 0 && length <= titleMaxLength;
    });

    const updateDynamicList = async () => {
      if (isNameComposing.value) return;

      // Validate title length
      const titleLength = countString(editingDynamicList.value.name || '');
      if (!editingDynamicList.value.name || titleLength > titleMaxLength) {
        return;
      }

      isLoadingDynamicList.value = true;
      const res = await dispatchUpdateDynamicList(store, {
        dynamicListId: dynamicListId,
        data: {
          name: editingDynamicList.value.name,
        },
      });
      if (res) {
        await fetchDynamicList();
        isEditingDynamicList.value = false;
      }
      isLoadingDynamicList.value = false;
    };

    const findDynamicListNote = (dynamicListId, dynamicListResult): string => {
      const note =
        dynamicListResult.find((x) => x.dynamic_list_id === dynamicListId)
          .note ?? '';
      return note;
    };

    const fetchDynamicListResults = async (): Promise<void> => {
      isLoadingSdses.value = true;
      companyFiles.value.splice(0);

      const res = await dispatchGetDynamicListResults(store, {
        dynamicListId: dynamicListId,
        data: {
          page: page.value,
          limit: limit.value,
          sortState: sortState.value,
          keyword_list: keywordList.value,
          pdf_name: filterCriteria.value.pdfName,
          product_name: filterCriteria.value.productName,
          note: filterCriteria.value.note,
          cause: filterCriteria.value.cause,
          revised_at_start_date: filterCriteria.value.revisedAtStartDate,
          revised_at_end_date: filterCriteria.value.revisedAtEndDate,
          created_at_start_date: filterCriteria.value.createdAtStartDate,
          created_at_end_date: filterCriteria.value.createdAtEndDate,
          updated_at_start_date: filterCriteria.value.updatedAtStartDate,
          updated_at_end_date: filterCriteria.value.updatedAtEndDate,
          user_id_list: filterCriteria.value.assigneeUsers.length
            ? filterCriteria.value.assigneeUsers.map((user) => user.uuid)
            : [],
          tag_id_list: filterCriteria.value.tags.length
            ? filterCriteria.value.tags.map((tag) => tag.id)
            : [],
          include_no_tag: filterCriteria.value.includeNoTag ?? false,
          department_id_list: filterCriteria.value.departmentIds.filter(
            (id) => id !== NO_DEPARTMENT_ID
          ),
          include_no_department:
            filterCriteria.value.departmentIds.includes(NO_DEPARTMENT_ID),
          is_new_checklist: filterCriteria.value.isNewChecklist,
          is_new_sds: filterCriteria.value.isNewSds,
          is_cause_updated: filterCriteria.value.isCauseUpdated,
        },
      });
      if (res) {
        total.value = res.data.total;
        filteredItemCount.value = res.data.total;
        companyFiles.value.splice(0);
        companyFiles.value.push(
          ...res.data.items.map((x) => {
            return {
              id: x.id,
              dynamic_list_id: x.dynamic_list_id,
              uuid: x.sds.uuid,
              name: x.sds.name,
              note: findDynamicListNote(
                x.dynamic_list_id,
                x.sds.dynamic_list_result
              ),
              cause: x.cause,
              created_user: x.sds.created_user,
              assignee_users: x.sds.assignee_users,
              departments: x.sds.departments,
              sds_products: x.sds.sds_products,
              revised_at: x.sds.revised_at,
              created_at: x.created_at,
              upload_at: x.sds.created_at,
              updated_at: x.updated_at,
              tags: x.sds.tags,
              is_new_checklist: x.is_new_checklist,
              is_new_sds: x.is_new_sds,
              is_cause_updated: x.is_cause_updated,
            };
          })
        );
      }

      isLoadingSdses.value = false;
    };

    const draftTagIds = computed<number[]>(() => {
      return tempFilterCriteria.value.tags.map((tag) => tag.id);
    });

    const updateSelectedTags = (tagIds: number[]): void => {
      const selectedTags = tags.value.filter((tag) => tagIds.includes(tag.id));
      tempFilterCriteria.value.tags = [...selectedTags];
    };

    const updateIncludeNoTag = (value: boolean): void => {
      tempFilterCriteria.value.includeNoTag = value;
    };

    const appliedTagIds = computed<number[]>(() => {
      return filterCriteria.value.tags.map((tag) => tag.id);
    });

    const tempFilterCriteriaUserId = computed(() => {
      const assigneeUsers = tempFilterCriteria.value.assigneeUsers;
      return Array.isArray(assigneeUsers)
        ? assigneeUsers.map((user) => user.uuid)
        : [];
    });

    const updateSelectedAssigneeUsers = (userIds: string[]): void => {
      const selectedUsers = users.value.filter((user) =>
        userIds.includes(user.uuid)
      );
      tempFilterCriteria.value.assigneeUsers = [...selectedUsers];
    };

    const updateSelectedDepartments = (departmentIds: string[]): void => {
      const includeNoDepartment =
        tempFilterCriteria.value.departmentIds.includes(NO_DEPARTMENT_ID);
      tempFilterCriteria.value.departmentIds = includeNoDepartment
        ? [NO_DEPARTMENT_ID, ...departmentIds]
        : departmentIds;
    };

    const removeAssigneeUserId = async (index: string): Promise<void> => {
      filterCriteria.value.assigneeUsers =
        filterCriteria.value.assigneeUsers.filter((value) => value !== index);
      tempFilterCriteria.value.assigneeUsers = [
        ...filterCriteria.value.assigneeUsers,
      ];
      applyFilters();
    };

    const removeTagsId = async (index: number): Promise<void> => {
      filterCriteria.value.tags = filterCriteria.value.tags.filter(
        (value) => value.id !== index
      );
      tempFilterCriteria.value.tags = [...filterCriteria.value.tags];
      applyFilters();
    };

    const removeNoTag = async (): Promise<void> => {
      filterCriteria.value.includeNoTag = false;
      tempFilterCriteria.value.includeNoTag = false;
      applyFilters();
    };

    const updateIncludeNoDepartment = (value: boolean): void => {
      const currentIds = tempFilterCriteria.value.departmentIds.filter(
        (id) => id !== NO_DEPARTMENT_ID
      );
      tempFilterCriteria.value.departmentIds = value
        ? [NO_DEPARTMENT_ID, ...currentIds]
        : currentIds;
    };

    const removeDepartmentId = async (id: string): Promise<void> => {
      filterCriteria.value.departmentIds =
        filterCriteria.value.departmentIds.filter((d) => d !== id);
      tempFilterCriteria.value.departmentIds = [
        ...filterCriteria.value.departmentIds,
      ];
      applyFilters();
    };

    const removePdfName = async (): Promise<void> => {
      filterCriteria.value.pdfName = '';
      tempFilterCriteria.value.pdfName = '';
      applyFilters();
    };

    const removeProductName = async (): Promise<void> => {
      filterCriteria.value.productName = '';
      tempFilterCriteria.value.productName = '';
      applyFilters();
    };

    const removeCause = async (): Promise<void> => {
      filterCriteria.value.cause = '';
      tempFilterCriteria.value.cause = '';
      applyFilters();
    };

    const removeNote = async (): Promise<void> => {
      filterCriteria.value.note = '';
      tempFilterCriteria.value.note = '';
      applyFilters();
    };

    const removeRevisedAtStartDate = async (): Promise<void> => {
      filterCriteria.value.revisedAtStartDate = '';
      tempFilterCriteria.value.revisedAtStartDate = '';
      applyFilters();
    };

    const removeRevisedAtEndDate = async (): Promise<void> => {
      filterCriteria.value.revisedAtEndDate = '';
      tempFilterCriteria.value.revisedAtEndDate = '';
      applyFilters();
    };

    const removeCreatedAtStartDate = async (): Promise<void> => {
      filterCriteria.value.createdAtStartDate = '';
      tempFilterCriteria.value.createdAtStartDate = '';
      applyFilters();
    };

    const removeCreatedAtEndDate = async (): Promise<void> => {
      filterCriteria.value.createdAtEndDate = '';
      tempFilterCriteria.value.createdAtEndDate = '';
      applyFilters();
    };

    const removeUpdatedAtStartDate = async (): Promise<void> => {
      filterCriteria.value.updatedAtStartDate = '';
      tempFilterCriteria.value.updatedAtStartDate = '';
      applyFilters();
    };

    const removeUpdatedAtEndDate = async (): Promise<void> => {
      filterCriteria.value.updatedAtEndDate = '';
      tempFilterCriteria.value.updatedAtEndDate = '';
      applyFilters();
    };

    const removeIsNewSds = async (): Promise<void> => {
      filterCriteria.value.isNewSds = false;
      tempFilterCriteria.value.isNewSds = false;
      applyFilters();
    };

    const removeIsNewChecklist = async (): Promise<void> => {
      filterCriteria.value.isNewChecklist = false;
      tempFilterCriteria.value.isNewChecklist = false;
      applyFilters();
    };

    const removeIsCauseUpdated = async (): Promise<void> => {
      filterCriteria.value.isCauseUpdated = false;
      tempFilterCriteria.value.isCauseUpdated = false;
      applyFilters();
    };

    const applyFilters = async (): Promise<void> => {
      resetPagenation();
      fetchDynamicListResults();
    };

    const setFilters = async (): Promise<void> => {
      filterCriteria.value = { ...tempFilterCriteria.value };
      toggleFilterPane();
      applyFilters();
    };

    const exportDynamicListResults = async (): Promise<void> => {
      const res = await dispatchGetExportDynamicListResults(store, {
        dynamicListId: dynamicListId,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `export_link_${dynamicListId}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    };

    const fetchDynamicLists = async () => {
      dynamicLists.value.splice(0);

      const res = await dispatchGetDynamicLists(store, {
        page: page.value - 1,
        limit: MAX_LIMIT,
        showAll: false,
      });
      if (res && res.data) {
        dynamicLists.value = [...res.data.items];
      }
    };

    const exportCreateSimple = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimple(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });

          const exportSds = companyFiles.value.find((x) => x.uuid === sdsId);
          if (exportSds) {
            const link = document.createElement('a');

            const fileName = removeLastPdfExtension(exportSds.name);
            const now = new Date();

            // 日本のタイムゾーンに変換
            const japanTime = new Intl.DateTimeFormat(locale, {
              timeZone: 'Asia/Tokyo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }).format(now);
            // YYYY-MM-DD HH:mm:ss 形式に整形
            const timestampString = japanTime
              .replace(/\//g, '-')
              .replace(/\s/, ' ')
              .replace(/:/g, '');

            link.href = URL.createObjectURL(blob);
            link.download = `${fileName}_${timestampString}_CREATE SIMPLE v2.5.1.zip`;
            link.click();
            URL.revokeObjectURL(link.href);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const exportCreateSimpleV3 = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimpleV3(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });

          const exportSds = companyFiles.value.find((x) => x.uuid === sdsId);
          if (exportSds) {
            const link = document.createElement('a');

            const fileName = removeLastPdfExtension(exportSds.name);
            const now = new Date();

            // 日本のタイムゾーンに変換
            const japanTime = new Intl.DateTimeFormat(locale, {
              timeZone: 'Asia/Tokyo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }).format(now);
            // YYYY-MM-DD HH:mm:ss 形式に整形
            const timestampString = japanTime
              .replace(/\//g, '-')
              .replace(/\s/, ' ')
              .replace(/:/g, '');

            link.href = URL.createObjectURL(blob);
            link.download = `${fileName}_${timestampString}_CREATE SIMPLE v3.1.2.zip`;
            link.click();
            URL.revokeObjectURL(link.href);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const exportCreateSimpleV3_2 = async (sdsId: string): Promise<void> => {
      try {
        dialogShow.value = true;

        const res = await dispatchGetExportCreateSimpleV3_2(store, {
          sdsId: sdsId,
        });
        if (res) {
          const blob = new Blob([res.data], {
            type: 'application/zip',
          });

          const exportSds = companyFiles.value.find((x) => x.uuid === sdsId);
          if (exportSds) {
            const link = document.createElement('a');

            const fileName = removeLastPdfExtension(exportSds.name);
            const now = new Date();

            const japanTime = new Intl.DateTimeFormat(locale, {
              timeZone: 'Asia/Tokyo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }).format(now);
            const timestampString = japanTime
              .replace(/\//g, '-')
              .replace(/\s/, ' ')
              .replace(/:/g, '');

            link.href = URL.createObjectURL(blob);
            link.download = `${fileName}_${timestampString}_CREATE SIMPLE v3.2.1.zip`;
            link.click();
            URL.revokeObjectURL(link.href);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        dialogShow.value = false;
      }
    };

    const editDynamicListResult = async (
      sdsId: string,
      dynamicListId: number,
      dynamicListResult: SdsDynamicListResult
    ): Promise<void> => {
      try {
        const res = await dispatchPutDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
          dynamicListResult: dynamicListResult,
        });
        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const addDynamicListResult = async (
      sdsId: string,
      dynamicListId: number
    ): Promise<void> => {
      try {
        const res = await dispatchPostDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
        });
        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const deleteDynamicListResult = async (
      sdsId: string,
      dynamicListId: number
    ): Promise<void> => {
      try {
        const res = await dispatchDeleteDynamicListResult(store, {
          sdsId: sdsId,
          dynamicListId: dynamicListId,
        });
        if (res) {
          await doFetchCompanySds(sdsId);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const filterCriteria: Ref<DynamicListResultFilterCriteria> = ref({
      assigneeUsers: [],
      tags: [],
      includeNoTag: false,
      departmentIds: [],
      cause: '',
      revisedAtStartDate: '',
      revisedAtEndDate: '',
      createdAtStartDate: '',
      createdAtEndDate: '',
      updatedAtStartDate: '',
      updatedAtEndDate: '',
      isNewChecklist: false,
      isNewSds: false,
      isCauseUpdated: false,
    });

    const tempFilterCriteria: Ref<DynamicListResultFilterCriteria> = ref({
      assigneeUsers: [],
      tags: [],
      includeNoTag: false,
      departmentIds: [],
      cause: '',
      revisedAtStartDate: '',
      revisedAtEndDate: '',
      createdAtStartDate: '',
      createdAtEndDate: '',
      updatedAtStartDate: '',
      updatedAtEndDate: '',
      isNewChecklist: false,
      isNewSds: false,
      isCauseUpdated: false,
    });

    const sortState = ref<SortState>({
      name: 'none',
      revised_at: 'none',
      updated_at: 'none',
      created_at: 'none',
    });

    const getFullName = (userId: string): string => {
      const user = users.value.find((user) => user.uuid === userId);
      return user
        ? `${user.last_name || 'Unknown'} ${user.first_name || ''}`
        : '';
    };

    const onClickProductName = (name: string): void => {
      filterCriteria.value.productName = name;
      applyFilters();
    };

    const onClickDepartment = (department: Department): void => {
      if (!filterCriteria.value.departmentIds.includes(department.uuid)) {
        filterCriteria.value.departmentIds.push(department.uuid);
        tempFilterCriteria.value.departmentIds = [
          ...filterCriteria.value.departmentIds,
        ];
        applyFilters();
      }
    };

    const onClickTag = (tag: TagResponse): void => {
      if (
        !filterCriteria.value.tags.some(
          (item: TagResponse) => item.id === tag.id
        )
      ) {
        filterCriteria.value.tags.push(tag);
        applyFilters();
      }
    };

    const toggleSort = (column: string): void => {
      if (sortState.value[column] === 'none') {
        sortState.value[column] = 'asc';
      } else if (sortState.value[column] === 'asc') {
        sortState.value[column] = 'desc';
      } else {
        sortState.value[column] = 'none';
      }
      Object.keys(sortState.value).forEach((key) => {
        if (key !== column) {
          sortState.value[key] = 'none';
        }
      });

      resetPagenation();
      fetchDynamicListResults();
    };

    const onPageChange = (newPage: number): void => {
      page.value = newPage;
      fetchDynamicListResults();
    };

    const onItemsPerPageChange = (newLimit: number): void => {
      limit.value = newLimit;
      resetPagenation();
      fetchDynamicListResults();
    };

    const filteredUsers = computed(() => {
      const usersWithFullName = users.value.map((user) => {
        const fullName = [user.last_name, user.middle_name, user.first_name]
          .filter(Boolean)
          .join(' ')
          .trim();

        return {
          value: user.uuid,
          title: fullName,
          searchTarget: fullName.toLowerCase(),
        };
      });

      if (searchUserText.value === '') {
        return usersWithFullName.map(({ value, title }) => ({ value, title }));
      }

      return usersWithFullName
        .filter((user) =>
          user.searchTarget.includes(searchUserText.value.toLowerCase())
        )
        .map(({ value, title }) => ({ value, title }));
    });

    onMounted(async (): Promise<void> => {
      downloadExportData();
      window.onbeforeunload = confirmSave;

      // uuid指定時はSDS一覧(SdsIndexPage)と同様にキーワードで該当SDSに絞り込む
      // 絞り込まないとページネーション後の1ページ目に無い uuid は下の find で
      // 見つからず、SDS詳細が開けない
      const uuid = router.currentRoute.query.uuid;
      if (uuid && typeof uuid === 'string') {
        keywordList.value.push(uuid);
      }

      // 独立したAPI呼び出しを全て並列実行
      await Promise.all([
        getCompanyTag(),
        getCompanyUsers({ includeSwitchable: true }),
        getCompanyDepartments(),
        getCompanyDepartmentTree(),
        fetchDynamicList().finally(() => {
          isLoadingDetail.value = false;
        }),
        fetchDynamicListResults(),
        fetchCompanySdsesCountWithAll(),
        fetchDynamicLists(),
      ]);

      // uuid指定時のみSDS詳細を表示
      if (router.currentRoute.query.uuid) {
        currentIndex.value = companyFiles.value.findIndex(
          (x) => x.uuid === router.currentRoute.query.uuid
        );
        const item = companyFiles.value.find(
          (x) => x.uuid === router.currentRoute.query.uuid
        );
        if (item) {
          await showSdsDetail(item, companyFiles.value.indexOf(item));
        }
      }
    });

    // Validation rules
    const titleRules = [required, isValidLength(titleMaxLength)];
    const descriptionRules = [isValidLength(descriptionMaxLength)];

    return {
      required,
      titleRules,
      descriptionRules,
      titleMaxLength,
      descriptionMaxLength,
      favoriteDialogOpen,
      isFavoriting,
      isUndoSnackbarVisible,
      onFavoriteButtonClick,
      confirmFavorite,
      undoUnfavorite,
      isTitleValid,
      isDescriptionValid,
      isLoadingDetail,
      sortState,
      toggleSort,
      onPageChange,
      onItemsPerPageChange,
      applyFilters,
      resetFilters,
      cancelFilters,
      setFilters,
      keywordList,
      pdfBlob,
      pdfName,
      limit,
      total,
      page,
      filterCriteria,
      tempFilterCriteria,
      toggleFilterPane,
      isDialogVisibleRevisedAtStartDate,
      isDialogVisibleRevisedAtEndDate,
      isDialogVisibleCreatedAtStartDate,
      isDialogVisibleCreatedAtEndDate,
      isDialogVisibleUpdatedAtStartDate,
      isDialogVisibleUpdatedAtEndDate,
      saveDate,
      isFilterPanelVisible,
      statusList,
      pdfDisplayOptions,
      sds,
      sdses,
      isLoadingSdses,
      isLoadingSds,
      isEditing,
      isEditingDynamicList,
      isLoadingDynamicList,
      isNameComposing,
      editingDynamicList,
      updateDynamicList,
      showSdsDetailView,
      productFormItems,
      materialFormItems,
      casFormItems,
      showPrevSds,
      showNextSds,
      hasPrevSds,
      hasNextSds,
      showSdsDetail,
      doUpdateCompanySds,
      cancelEdit,
      resetPagenation,
      resetSearchForm,
      doFetchCompanySdses,
      doFetchCompanySdsesCount,
      doFetchCompanySdsesCountWithCondition,
      doFetchCompanySds,
      doFetchPdf,
      downloadExportData,
      fetchCompanySdsesCountWithAll,
      fetchDynamicList,
      fetchDynamicListResults,
      dynamicList,
      formatToDate,
      formatDatePickerDay,
      exportDynamicListResults,
      uploadedTotal,
      filteredItemCount,
      lastCount,
      companyFiles,
      nextUpdateDate,
      exportCreateSimple,
      exportCreateSimpleV3,
      exportCreateSimpleV3_2,
      dialogShow,
      cancelExportCreateSimple,
      canExportCreateSimpleCount,
      canExportCreateSimpleCountV3,
      reanalysisSdsModalShow,
      openReanalysisSdsModal,
      closeReanalysisSdsModal,
      reanalysisSds,
      reanalysisSdsComment,
      reanalysisRequestTypeItems,
      reanalysisCommentPlaceholder,
      dynamicLists,
      fetchDynamicLists,
      editDynamicListResult,
      addDynamicListResult,
      deleteDynamicListResult,
      activeSds,
      hasPermission,
      departments,
      findDynamicListNote,
      useNewRegulatoryScreeningUI,
      isCreatedByChemicanAdmin,
      isEditingDescription,
      editedDescription,
      isLoadingDescription,
      startEditingDescription,
      cancelEditingDescription,
      saveDescription,
      isChecklistLawRegulationEnabled,
      isBeforeStart,
      hasExtractionConditions,
      hasRuleTree,
      ruleTreeConditions,
      lawNames,
      descriptionParts,
      regulationNames,
      dbtTypeLabels,
      hasThreshold,
      regulationDatabaseLinks,
      statusTextList,
      hasFilterCriteria,
      removeAssigneeUserId,
      removeTagsId,
      removeNoTag,
      removeDepartmentId,
      updateIncludeNoDepartment,
      removePdfName,
      removeProductName,
      removeCause,
      removeNote,
      removeRevisedAtStartDate,
      removeRevisedAtEndDate,
      removeCreatedAtStartDate,
      removeCreatedAtEndDate,
      removeUpdatedAtStartDate,
      removeUpdatedAtEndDate,
      removeIsNewSds,
      removeIsNewChecklist,
      removeIsCauseUpdated,
      getFullName,
      getFullNameUtil,
      searchUserText,
      searchTagText,
      appliedTagIds,
      tempFilterCriteriaUserId,
      filteredUsers,
      onClickProductName,
      onClickDepartment,
      onClickTag,
      StatusId,
      P,
      companyDepartments,
      departmentTree,
      visibleCompanyDepartmentsByOwnOrRole,
      sharedMessages,
      NO_TAG_ID,
      NO_DEPARTMENT_ID,
      draftTagIds,
      updateSelectedTags,
      updateIncludeNoTag,
      updateSelectedAssigneeUsers,
      updateSelectedDepartments,
      m,
      locale,
      users,
    };
  },
});
